import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
} from "@mui/material";
import classNames from "classnames";
import { useEffect, useRef, useState, type FC } from "react";
import { ArrowIcon } from "../../../../../shared/icons/ArrowIcon";
import { DotsIcon } from "../../../../../shared/icons/DotsIcon";
import { FolderCloseIcon } from "../../../../../shared/icons/FolderCloseIcon";
import { FolderOpenIcon } from "../../../../../shared/icons/FolderOpenIcon";
import { TrashIcon } from "../../../../../shared/icons/TrashIcon";
import styles from "../../../styles.module.css";
import { AppTooltip } from "../../../../../shared/components/AppTooltip";

interface Props {
  child: React.ReactNode;
  isOpen?: boolean;
  selectedId?: string | null;
  isSendDisabled?: boolean;
  isFolderEmpty?: boolean;
  itemName?: string;
  onSelect: (id: string) => void;
  onDelete?: (id: string, name: string) => void;
  onToggle?: () => void;
  onOpenModalNameChange?: () => void;
  className?: string;
}

export const SidebarFolderItem: FC<Props> = ({
  child,
  isOpen,
  selectedId,
  itemName,
  isSendDisabled,
  isFolderEmpty,
  onSelect,
  onDelete,
  onToggle,
  onOpenModalNameChange,
  className,
}) => {
  const [isLongItemName, setIsLongItemName] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState<boolean>(false);

  useEffect(() => {
    if (textRef.current) {
      const { scrollWidth, clientWidth } = textRef.current;
      setIsLongItemName(scrollWidth > clientWidth);
    }
  }, [itemName]);

  const folderIcon = isOpen ? (
    <FolderOpenIcon className={styles["folder-icon"]} />
  ) : (
    <FolderCloseIcon className={styles["folder-icon"]} />
  );

  return (
    <li className={styles.folder}>
      <Accordion
        expanded={isFolderEmpty ? false : isOpen}
        onChange={onToggle}
        classes={{
          expanded: styles["expanded-folder"],
        }}
        className={styles.accordion}
      >
        <AccordionSummary
          classes={{
            expanded: styles["expanded-summary-sidebar"],
          }}
          className={styles["accordion-summary"]}
          aria-controls="panel-content"
          id="panel-header"
        >
          <div
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            className={classNames(
              styles["folder-name"],
              {
                [styles.disabled]: isSendDisabled,
              },
              className,
            )}
            onClick={() => onSelect(selectedId as string)}
          >
            <AppTooltip
              classes={{ tooltip: styles["tooltip"] }}
              title={itemName}
              open={isHovering && isLongItemName}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [-10, 5],
                      },
                    },
                  ],
                },
              }}
            >
              <div className={styles["folder-name-block"]}>
                {folderIcon}
                <div ref={textRef} className={styles["folder-item-name"]}>
                  {itemName}
                </div>
              </div>
            </AppTooltip>
            <div className={styles["right-button-container"]}>
              <IconButton
                onClick={onOpenModalNameChange}
                className={styles["edit-button"]}
              >
                <DotsIcon />
              </IconButton>
              {isFolderEmpty ? (
                <IconButton
                  className={classNames(styles["delete-button"], {
                    [styles.disabled]: isSendDisabled,
                  })}
                  onClick={() =>
                    onDelete?.(selectedId as string, itemName as string)
                  }
                >
                  <TrashIcon />
                </IconButton>
              ) : (
                <ArrowIcon className={styles["folder-arrow"]} />
              )}
            </div>
          </div>
        </AccordionSummary>
        <AccordionDetails className={styles["accordion-details"]}>
          <ul className={styles["object-list"]}>{child}</ul>
        </AccordionDetails>
      </Accordion>
    </li>
  );
};
